import React, { useEffect, useState } from 'react';
import { X, Zap, WifiOff, Bell, Download } from 'lucide-react';
import { usePWA } from '../../hooks/usePWA';

const DISMISS_KEY = 'clarity_news_pwa_dismissed_at';
const COOLDOWN_MS = 7 * 24 * 60 * 60 * 1000;

const InstallPrompt: React.FC = () => {
  const { canInstall, isInstalled, install } = usePWA();
  const [dismissed, setDismissed] = useState<boolean>(true);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(DISMISS_KEY);
      if (!raw) {
        setDismissed(false);
        return;
      }
      const ts = parseInt(raw, 10);
      if (Number.isNaN(ts) || Date.now() - ts > COOLDOWN_MS) {
        setDismissed(false);
      } else {
        setDismissed(true);
      }
    } catch {
      setDismissed(false);
    }
  }, []);

  if (!canInstall || isInstalled || dismissed) return null;

  const handleDismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, Date.now().toString());
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  const handleInstall = async () => {
    setInstalling(true);
    const accepted = await install();
    setInstalling(false);
    if (!accepted) {
      handleDismiss();
    }
  };

  return (
    <div
      role="dialog"
      aria-label="Install Clarity News"
      className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-4 sm:w-96 z-[60] animate-slide-in-up"
    >
      <div className="relative rounded-2xl bg-gradient-to-br from-[#1a1a1a] via-[#242424] to-[#1a1a1a] shadow-2xl border border-white/10 overflow-hidden">
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#B6AC7D] via-amber-400 to-[#B6AC7D]" />
        <button
          onClick={handleDismiss}
          aria-label="Dismiss install prompt"
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-white/5 hover:bg-white/15 text-white/70 hover:text-white transition-colors flex items-center justify-center"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="p-5 pt-6">
          <div className="flex items-start gap-3 mb-4">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-[#B6AC7D] to-[#8f8760] flex items-center justify-center flex-shrink-0 shadow-lg">
              <Download className="w-6 h-6 text-white" />
            </div>
            <div className="min-w-0 pr-6">
              <h3 className="text-white font-bold text-base leading-tight">
                Install Clarity News
              </h3>
              <p className="text-white/60 text-xs mt-0.5">
                Get the full experience on your device
              </p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 mb-5">
            <div className="flex flex-col items-center text-center px-2 py-3 rounded-xl bg-white/5 border border-white/5">
              <Zap className="w-4 h-4 text-[#B6AC7D] mb-1.5" />
              <span className="text-[10px] font-semibold text-white/80 uppercase tracking-wider">
                Faster
              </span>
            </div>
            <div className="flex flex-col items-center text-center px-2 py-3 rounded-xl bg-white/5 border border-white/5">
              <WifiOff className="w-4 h-4 text-[#B6AC7D] mb-1.5" />
              <span className="text-[10px] font-semibold text-white/80 uppercase tracking-wider">
                Offline
              </span>
            </div>
            <div className="flex flex-col items-center text-center px-2 py-3 rounded-xl bg-white/5 border border-white/5">
              <Bell className="w-4 h-4 text-[#B6AC7D] mb-1.5" />
              <span className="text-[10px] font-semibold text-white/80 uppercase tracking-wider">
                Alerts
              </span>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <button
              onClick={handleInstall}
              disabled={installing}
              className="flex-1 px-4 py-2.5 rounded-xl bg-gradient-to-r from-[#B6AC7D] to-[#A49C6D] hover:from-[#A49C6D] hover:to-[#8f8760] text-white text-sm font-semibold shadow-lg transition-all disabled:opacity-60"
            >
              {installing ? 'Installing…' : 'Install App'}
            </button>
            <button
              onClick={handleDismiss}
              className="px-4 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-white/80 text-sm font-medium transition-colors"
            >
              Later
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InstallPrompt;
